import { type Dispatch, type ReactNode, type SetStateAction, useState } from 'react';
import { connectToEngineSocket, disconnectFromEngineSocket } from '../../mcr/signalRConnection';
import { publishTheme } from '../../sources/theme';
import type { TenantRuntime } from '../../types/Tenant';
import ActivateRuntime from '../flow/ActivateRuntime';
import ErrorBoundary from '../generic/ErrorBoundary';
import GenericModal from '../generic/modal/GenericModal';
import Loader from '../loader/Loader';
import { tryGetTraceId } from '../../utils/error';
import ErrorTraceId from '../generic/ErrorTraceId';

interface Props {
    showPublishModal: boolean;
    setShowPublishModal: Dispatch<SetStateAction<boolean>>;
    container?: HTMLDivElement | undefined;
    themeName: string;
    runtimes: TenantRuntime[] | null;
}

const PublishThemeModal = ({
    showPublishModal,
    setShowPublishModal,
    container,
    themeName,
    runtimes,
}: Props) => {
    const [error, setError] = useState<string | null>(null);
    const [errorTraceId, setErrorTraceId] = useState<string | null>(null);
    const [runtime, setRuntime] = useState<TenantRuntime | undefined>(undefined);
    const [comment, setComment] = useState('');
    const [viewState, setViewState] = useState<string>('PUBLISH');
    const closeModal = () => {
        setShowPublishModal(false);
        setViewState('PUBLISH');
    };

    const sendBuildThemeRequest = async () => {
        try {
            if (runtime?.id) {
                setViewState('LOADING');
                const messageId: string = crypto.randomUUID();
                const { connectionReady, result } = connectToEngineSocket();

                // Wait for connection to be ready before sending notification
                await connectionReady;

                await publishTheme({
                    runtime: {
                        id: runtime.id,
                        isActive: true,
                        isDefault: true,
                        messageId,
                    },
                    themeName,
                });

                try {
                    const deployResult = await result;

                    if (deployResult.messageId !== messageId) {
                        throw new Error('Returned Multi Cloud Runtime message ID does not match');
                    }
                    if (!deployResult.success) {
                        throw new Error(deployResult.message);
                    }
                    setViewState('SUCCESS');
                } catch (e) {
                    setError((e as Error).message);
                    setErrorTraceId(tryGetTraceId(e));
                    setViewState('FAILED');
                } finally {
                    await disconnectFromEngineSocket();
                }
            } else {
            }
        } catch (e) {
            setError((e as Error).message);
            setErrorTraceId(tryGetTraceId(e));
            setViewState('FAILED');
        }
    };
    const onRuntimeChanged = (runtime: TenantRuntime | undefined) => setRuntime(runtime);
    let body: ReactNode;
    let footer: ReactNode;
    switch (viewState) {
        case 'LOADING': {
            body = <Loader />;
            footer = null;
            break;
        }
        case 'PUBLISH': {
            body = (
                <ErrorBoundary>
                    <ActivateRuntime
                        runtimes={runtimes}
                        onChange={onRuntimeChanged}
                        comment={comment}
                        onCommentChanged={(e) => setComment(e.currentTarget.value)}
                    />
                </ErrorBoundary>
            );
            footer = (
                <>
                    <button
                        type="button"
                        onClick={closeModal}
                        className="btn btn-default"
                        data-testid="close-modal-button"
                    >
                        Close
                    </button>
                    <button
                        type="button"
                        onClick={sendBuildThemeRequest}
                        className="btn btn-primary"
                    >
                        Publish Theme
                    </button>
                </>
            );
            break;
        }
        case 'FAILED': {
            body = (
                <>
                    <h3>{`Publishing to MCR failed with the error: ${error}.`}</h3>
                    <ErrorTraceId traceId={errorTraceId} />
                </>
            );
            footer = (
                <button
                    type="button"
                    onClick={closeModal}
                    className="btn btn-primary"
                    data-testid="close-modal-button"
                >
                    Close
                </button>
            );
            break;
        }
        case 'SUCCESS': {
            body = (
                <h3>
                    Theme: {themeName} has successfully been published to {runtime?.developerName}
                </h3>
            );
            footer = (
                <button
                    type="button"
                    onClick={closeModal}
                    className="btn btn-primary"
                    data-testid="close-modal-button"
                >
                    Close
                </button>
            );
            break;
        }
    }
    return (
        <GenericModal
            container={container}
            title="Publish Theme to MCR"
            onHide={closeModal}
            show={showPublishModal}
            renderBody={() => <>{body}</>}
            renderFooter={() => <>{footer}</>}
        />
    );
};

export default PublishThemeModal;
